/**
 * The half of the in-call controls that is SERVER state: hang up and hold.
 *
 * Mute and camera are pure media and never leave the browser; hold and hangup
 * change what the other party sees and must go through the API, or a call the
 * user ended keeps a room alive and the history row never closes.
 */
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import { presenceActions } from '@/features/presence/presenceStore';
import { callsApi } from '../data/callsApi';
import { CALL_KEYS } from '../data/queryKeys';
import { releaseCallMedia } from '../media/callMediaRegistry';
import { activeCallActions } from '../store/activeCallStore';

export interface CallControls {
  readonly onHold: boolean;
  readonly isHangingUp: boolean;
  toggleHold(): void;
  hangup(): void;
}

export function useCallControls(callId: string, onEnded: () => void): CallControls {
  const [onHold, setOnHold] = useState(false);
  const queryClient = useQueryClient();

  const hold = useMutation({
    mutationFn: (next: boolean) => callsApi.hold(callId, next),
    onSuccess: (result) => setOnHold(result.onHold),
    onError: () => toast.error('Could not change hold. Try again.'),
  });

  const hangup = useMutation({
    mutationFn: () => callsApi.hangup(callId),
    /* The end screen must appear even if the request failed: the user has
       already stopped talking, and leaving them in a dead room is worse than
       a server-side row that the ring timeout will close anyway. */
    onSettled: () => {
      /* Hanging up is the exit that leaves the page MOUNTED — the ended screen
         renders in its place — so presence is dropped on the action itself
         rather than on a teardown that has not happened. Idempotent with the
         same clear in useCallLifecycle; whichever runs first wins and the other
         is a no-op. */
      presenceActions.setFlags({ inCall: false });
      /* AND GIVE THE DEVICES BACK, HERE, RATHER THAN WHEN THE TREE GETS ROUND
         TO IT. Swapping the room for the ended screen unmounts `<CallRoom>`
         and that disconnects — but the mic is the one thing that must not
         depend on a render happening, so the exit is taken by name. Idempotent
         with the same release in `useCallLifecycle`. */
      void releaseCallMedia(callId);
      activeCallActions.clear(callId);
      void queryClient.invalidateQueries({ queryKey: CALL_KEYS.all });
      onEnded();
    },
  });

  return {
    onHold,
    isHangingUp: hangup.isPending,
    toggleHold: useCallback(() => hold.mutate(!onHold), [hold, onHold]),
    hangup: useCallback(() => hangup.mutate(), [hangup]),
  };
}
